import type { RowData } from "@tanstack/react-table";
import type { GridController } from "../../focus/gridController";
import type { DataGridReactTable } from "../../types";
import { type DragInput, getDragInput } from "./dragKind";
import {
    getLabelIndent,
    getRowTargetAt,
    type RowDragSnapshot,
    toDropRows,
} from "./rowDrag";
import {
    type DataGridRowMove,
    getKeyboardDropTargets,
    type RowDropTarget,
} from "./rowDropTarget";

export interface RowDragSession {
    rowId: string;
    label: string;
    snapshot: RowDragSnapshot;
    keyboardTargets: RowDropTarget[];
    target: RowDropTarget | null;
    input: DragInput;
    indent: number;
    cleanup: () => void;
}

/**
 * A drag of one row, snapshotting the shown rows. Pointer drags follow the
 * pointer and the viewport's scroll until `cleanup`; keyboard drags list
 * every target up front.
 */
export const createRowDragSession = <TData extends RowData>({
    table,
    controller,
    rowId,
    label,
    isTree,
    canDrop,
    activatorEvent,
    setTarget,
}: {
    table: DataGridReactTable<TData>;
    controller: GridController;
    rowId: string;
    label: string;
    isTree: boolean;
    canDrop: ((move: DataGridRowMove) => boolean) | undefined;
    activatorEvent: Event | null;
    setTarget: (session: RowDragSession, target: RowDropTarget | null) => void;
}): RowDragSession => {
    const input = getDragInput(activatorEvent);
    const { viewport } = controller;
    const snapshot: RowDragSnapshot = {
        rows: toDropRows(table.getRowModel().rows),
        sourceIndex: controller.rowIndexById.get(rowId) ?? -1,
        isTree,
        canDrop,
    };
    const session: RowDragSession = {
        rowId,
        label,
        snapshot,
        keyboardTargets:
            input === "keyboard" ? getKeyboardDropTargets(snapshot) : [],
        target: null,
        input,
        indent: viewport ? getLabelIndent({ table, viewport }) : 0,
        cleanup: () => undefined,
    };
    if (input === "keyboard" || !viewport) {
        return session;
    }
    let clientY =
        activatorEvent instanceof PointerEvent ? activatorEvent.clientY : 0;
    const update = () =>
        setTarget(session, getRowTargetAt({ controller, snapshot, clientY }));
    const handleMove = (event: PointerEvent) => {
        clientY = event.clientY;
        update();
    };
    document.addEventListener("pointermove", handleMove);
    viewport.addEventListener("scroll", update, { passive: true });
    session.cleanup = () => {
        document.removeEventListener("pointermove", handleMove);
        viewport.removeEventListener("scroll", update);
    };
    return session;
};
